import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';
//
import { setSession } from './utils';
import { getOrCreateDeviceId } from '../utils/helpers';
// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  if (action.type === 'DELETE_ACCOUNT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const storageAvailable = localStorageAvailable();
  const sanitizeUserForStorage = (user) => {
    if (!user) return null;
    const {
      permissions,
      permissions_names,
      // أي حقول أخرى حساسة لا تريد تخزينها:
      // roles, roles_names (اختياري حسب رغبتك)
      ...safe
    } = user;
    return safe;
  };

  // const initialize = useCallback(async () => {
  //   try {
  //     const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
  //     const user = storageAvailable ? JSON.parse(localStorage.getItem('user')) : '';

  //     if (accessToken && user) {
  //       setSession(accessToken);

  //       dispatch({
  //         type: 'INITIAL',
  //         payload: {
  //           isAuthenticated: true,
  //           user,
  //         },
  //       });
  //     } else {
  //       dispatch({
  //         type: 'INITIAL',
  //         payload: {
  //           isAuthenticated: false,
  //           user: null,
  //         },
  //       });
  //     }
  //   } catch (error) {
  //     console.error(error);
  //     dispatch({
  //       type: 'INITIAL',
  //       payload: {
  //         isAuthenticated: false,
  //         user: null,
  //       },
  //     });
  //   }
  // }, [storageAvailable]);
  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';

      if (accessToken) {
        // ضبط التوكن في axios
        setSession(accessToken);

        // طلب بيانات المستخدم المصدَّق من /auth/me
        const res = await axios.get('/auth/me');
        const user = res?.data?.data;

        if (user) {
          // تخزين بيانات المستخدم المحدثة
          if (storageAvailable) {
            // localStorage.setItem('user', JSON.stringify(user));
            const safeUser = sanitizeUserForStorage(user);
            localStorage.setItem('user', JSON.stringify(safeUser));

          }

          dispatch({
            type: 'INITIAL',
            payload: {
              isAuthenticated: true,
              user,
            },
          });
          return;
        }
      }

      // في حال عدم وجود توكن أو عدم وجود يوزر صحيح
      setSession(null);
      if (storageAvailable) {
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
      }
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    } catch (error) {
      console.error('🚀 ~ initialize error:', error);

      // لو فشل /auth/me (توكن منتهي مثلًا) اعتبر المستخدم غير مسجل
      setSession(null);
      if (storageAvailable) {
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
      }
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // REGISTER
  // const register = useCallback(async (full_name, email, phone_number, password, password_confirmation, role) => {

  //   const response = await axios.post('/signup', { full_name, email, phone_number, password, password_confirmation, role });
  //   console.log("🚀 ~ AuthProvider ~ response:", response)

  //   const { token: access_token, ...user } = response?.data;
  //   setSession(access_token);
  //   localStorage.setItem('user', JSON.stringify(user));
  //   localStorage.setItem('accessToken', access_token);

  //   dispatch({
  //     type: 'REGISTER',
  //     payload: {
  //       user
  //     },
  //   });
  //   return response
  // }, []);


  // const login = useCallback(async (email, password) => {
  //   try {
  //     const payload = {
  //       email,
  //       password,
  //     };
  //     const response = await axios.post('/auth/login', payload);
  //     const { token_type, token, ...user } = response?.data?.data;
  //     setSession(`${token}`);
  //     localStorage.setItem('user', JSON.stringify(user));
  //     localStorage.setItem('accessToken', `${token}`);
  //     dispatch({
  //       type: 'LOGIN',
  //       payload: { user, },
  //     });

  //     return { status: 'success', message: response?.data?.message || 'Login successful', data: user };
  //   } catch (error) {
  //     console.error("🚀 ~ login error:", error);
  //     return {
  //       status: 'error',
  //       message: error?.response?.data?.message || 'البريد الألكتروني او كلمة المرور غير صحيحة',
  //     };
  //   }
  // }, []);

  const login = useCallback(async (email, password) => {
    try {
      // الحصول على أو إنشاء Device ID
      const deviceId = getOrCreateDeviceId();

      // إنشاء device_name بالصيغة المطلوبة
      const deviceName = `dashboard-${email}-${deviceId}`;

      const payload = {
        email,
        password,
        device_name: deviceName
      };

      const response = await axios.post('/auth/login', payload);
      const { token } = response?.data?.data;

      setSession(`${token}`);
      localStorage.setItem('accessToken', `${token}`);
      const meRes = await axios.get('/auth/me');
      const user = meRes?.data?.data;
      
      localStorage.setItem('device_name', deviceName);
      const safeUser = sanitizeUserForStorage(user);
      localStorage.setItem('user', JSON.stringify(safeUser));

      localStorage.setItem('roles', JSON.stringify(user.roles));
      localStorage.setItem('roles_names', JSON.stringify(user.roles_names));

      dispatch({
        type: 'LOGIN',
        payload: { user },
      });

      return {
        status: 'success',
        message: response?.data?.message || 'Login successful',
        data: user,
      };
    } catch (error) {
      console.error('🚀 ~ login error:', error);
      return {
        status: 'error',
        message:
          error?.response?.data?.message ||
          'البريد الألكتروني او كلمة المرور غير صحيحة',
      };
    }
  }, []);


  // LOGOUT
  const logout = useCallback(async () => {
    const res = await axios.post('/auth/logout');
    setSession(null);
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    dispatch({
      type: 'LOGOUT',
    });
    return res;
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'jwt',
      // register,
      login,
      logout
    }),
    [state.isAuthenticated, state.isInitialized, state.user, login, logout]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
